import { Component, inject, ViewChild, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { Router } from '@angular/router';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';

import { UploadShapefileComponent } from '../../../shared/components/upload-shapefile/upload-shapefile.component';
import { FileParamsFormComponent } from '../../../shared/components/file-params-form/file-params-form.component';
import { EnhancedLoadingService } from '../../../shared/services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../../shared/services/smart-orpc-client.service';
import { FeatureFlagsService } from '../../../shared/services/feature-flags.service';
import { SnackbarService } from '../../../shared/services/snackbar.service';

@Component({
  selector: 'app-monitoring-safra-new',
  imports: [
    UploadShapefileComponent,
    FileParamsFormComponent,
    MatIconModule,
    MatDividerModule,
    MatButtonModule,
    MatSelectModule,
    ReactiveFormsModule,
  ],
  templateUrl: './monitoring-safra-new.component.html',
  styleUrl: './monitoring-safra-new.component.scss',
})
export class MonitoringSafraNewComponent implements OnInit {
  private readonly smartClient = inject(SmartOrpcClientService);
  private readonly loadingService = inject(EnhancedLoadingService);
  private readonly featureFlags = inject(FeatureFlagsService);
  private readonly snackBar = inject(SnackbarService);

  // Observables para diferentes tipos de loading
  globalLoading$ = this.loadingService.global$;
  gridLoading$ = this.loadingService.grid$;
  inputLoading$ = this.loadingService.input$;
  buttonLoading$ = this.loadingService.button$;

  @ViewChild(FileParamsFormComponent)
  declare monitoringFileParamsComponent: FileParamsFormComponent;

  @ViewChild(UploadShapefileComponent)
  declare uploadComponent: UploadShapefileComponent;

  uploadedFileName: string | null = null;
  originalFileName: string | null = null;
  router = inject(Router);

  protected cropPeriods: string[] = [];
  protected cropCultures: {
    id: number;
    description: string;
    enable: boolean;
  }[] = [];

  // Expor para o template
  get enabledFileTypes() {
    return this.featureFlags.getMonitoramentoEnabledFileTypes();
  }

  form = new FormGroup({
    cropPeriod: new FormControl('', {
      validators: [Validators.required],
    }),
    cropCulture: new FormControl<{
      id: number;
      description: string;
      enable: boolean;
    } | null>(null, {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });

  get isParamsInvalid(): boolean {
    if (
      this.form.invalid ||
      !this.uploadComponent?.fileCtrl.valid ||
      !this.uploadedFileName
    ) {
      return true;
    }

    // Validar formulário de parâmetros considerando campos desabilitados (Excel)
    const monitoringForm = this.monitoringFileParamsComponent?.form;
    if (!monitoringForm) {
      return true;
    }

    // Verificar se o campo obrigatório featurePrimaryKey está preenchido
    // usando getRawValue() para pegar valores mesmo quando desabilitado
    const rawValues = monitoringForm.getRawValue();
    if (!rawValues.featurePrimaryKey) {
      return true;
    }

    // Verificar se há erros nos controles (exceto erros de "disabled")
    const primaryKeyControl = monitoringForm.controls.featurePrimaryKey;
    if (primaryKeyControl.errors && !primaryKeyControl.disabled) {
      return true;
    }

    return false;
  }

  ngOnInit(): void {
    const now = new Date();

    this.cropPeriods = [
      `${now.getFullYear() - 2}/${now.getFullYear() - 1}`,
      `${now.getFullYear() - 1}/${now.getFullYear()}`,
      `${now.getFullYear()}/${now.getFullYear()}`,
      `${now.getFullYear()}/${now.getFullYear() + 1}`,
      `${now.getFullYear() + 1}/${now.getFullYear() + 1}`,
    ];

    this.getCropCulturesData();
  }

  async onSubmit(): Promise<void> {
    this.monitoringFileParamsComponent.form.markAllAsTouched();
    this.form.markAllAsTouched();

    if (this.isParamsInvalid) {
      return;
    }

    // Usar getRawValue() para obter valores de campos desabilitados (Excel)
    const {
      featurePrimaryKey,
      producer,
      contract,
      car,
      insurancePolicy,
      farmName,
      identityDocument,
    } = this.monitoringFileParamsComponent.form.getRawValue();

    const result = await this.smartClient.client.monitoring.request({
      fileName: this.uploadedFileName!,
      originalFileName: this.originalFileName ?? undefined,
      featurePrimaryKeyField: featurePrimaryKey!,
      identityDocumentField: identityDocument ?? undefined,
      producerField: producer ?? undefined,
      contractFieldKey: contract ?? undefined,
      carFieldKey: car ?? undefined,
      insurancePolicyKey: insurancePolicy ?? undefined,
      cropYear: this.form.controls.cropPeriod.value!,
      customerFarmNameKey: farmName ?? undefined,
      targetCulture: this.form.controls.cropCulture.value ?? undefined,
    });

    this.snackBar.alert(
      `Solicitação ${result.requestId} registrada com sucesso.`,
      -1,
    );

    this.navigateToList();
  }

  onFileUploaded(fileName: string | null): void {
    this.uploadedFileName = fileName;
  }

  onOriginalFileName(originalName: string | null): void {
    this.originalFileName = originalName;
  }

  navigateToList(): void {
    this.router.navigate(['/monitoring-safras']);
  }

  private async getCropCulturesData(): Promise<void> {
    try {
      const cropCultures =
        await this.smartClient.client.monitoring.cultures.list({});

      this.cropCultures = cropCultures;
    } catch (error) {
      console.error('Erro getCulturesData():', error);
    }
  }
}
